import React, {useEffect, useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import {validateNovaFile} from '../shared/novaFileTypes';

export interface NovaReferenceFile {
	fileId: string;
	filename: string;
	variableToken: string;
	processingStatus: string;
	sizeBytes: number | null;
}

export interface NovaReferenceFilesProps {
	files: NovaReferenceFile[];
	// Uploads the file, reporting its progress as a fraction from 0 to 1 when the caller asks for it.
	onAttach: (file: File, onProgress?: (fraction: number) => void) => Promise<void>;
	onRemove: (fileId: string) => Promise<void>;
}

// A file on its way up, shown as its own row until the upload settles. A failed one stays, so the
// author can retry it or remove it.
export interface NovaPendingUpload {
	key: number;
	file: File;
	progress: number;
	failed: boolean;
}

interface UseNovaReferenceFilesOptions extends Omit<NovaReferenceFilesProps, 'files'> {
	// Show each upload as a row with its progress, and a failed one with Retry and Remove, instead
	// of a single "Uploading…" line and an error message.
	trackUploads?: boolean;
}

const COPY_FEEDBACK_MS = 2000;

export function formatBytes(bytes: number | null): string {
	if (!bytes || bytes <= 0) {
		return '';
	}
	if (bytes < 1024) {
		return `${bytes} B`;
	}
	if (bytes < 1024 * 1024) {
		return `${Math.round(bytes / 1024)} KB`;
	}
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// What the Files section does, whichever layout shows it: attach by drop or Browse, remove, and copy a
// ready file's token. One operation runs at a time. An attach, remove or copy that settles after the
// section has gone (the author switched tab or agent) is dropped rather than set on an unmounted
// component.
export function useNovaReferenceFiles({onAttach, onRemove, trackUploads = false}: UseNovaReferenceFilesOptions) {
	const {formatMessage} = useIntl();
	const inputRef = useRef<HTMLInputElement>(null);
	const [dragActive, setDragActive] = useState(false);
	const [isBusy, setIsBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);
	// The guard for "one operation at a time" has to be a ref, not the isBusy state: React batches
	// state updates, so two events dispatched before the next render (a drop landing in the same batch
	// as a picker change) would both read isBusy as false and both start. The state exists to drive the
	// UI (button labels and disabled states); this decides whether an operation may begin.
	const inFlightRef = useRef(false);
	const isMountedRef = useRef(true);
	// The outcome of the last copy, shown beside that file's token for a moment.
	const [copied, setCopied] = useState<{fileId: string; ok: boolean} | null>(null);
	const [pendingUploads, setPendingUploads] = useState<NovaPendingUpload[]>([]);
	const nextUploadKeyRef = useRef(0);

	useEffect(() => {
		isMountedRef.current = true;
		return () => {
			isMountedRef.current = false;
		};
	}, []);

	useEffect(() => {
		if (!copied) {
			return undefined;
		}
		const timer = setTimeout(() => setCopied(null), COPY_FEEDBACK_MS);
		return () => clearTimeout(timer);
	}, [copied]);

	const whileMounted =
		<T>(apply: (value: T) => void) =>
		(value: T): void => {
			if (isMountedRef.current) {
				apply(value);
			}
		};

	// The clipboard is missing outside a secure context, and writing to it is refused without
	// permission or while the page is not focused; either way the author is told, never left guessing.
	const copyToken = (file: NovaReferenceFile): void => {
		const write = navigator.clipboard?.writeText(file.variableToken);
		if (!write) {
			setCopied({fileId: file.fileId, ok: false});
			return;
		}
		write.then(
			whileMounted(() => setCopied({fileId: file.fileId, ok: true})),
			whileMounted(() => setCopied({fileId: file.fileId, ok: false}))
		);
	};

	const beginOperation = (): boolean => {
		if (inFlightRef.current) {
			return false;
		}
		inFlightRef.current = true;
		setError(null);
		setIsBusy(true);
		return true;
	};

	const endOperation = (): void => {
		inFlightRef.current = false;
		if (isMountedRef.current) {
			setIsBusy(false);
		}
	};

	const updatePending = (key: number, change: Partial<NovaPendingUpload>): void => {
		if (isMountedRef.current) {
			setPendingUploads(uploads => uploads.map(pending => (pending.key === key ? {...pending, ...change} : pending)));
		}
	};

	// Uploads a file as the row `key`, adding the row or, on a retry, resetting it. The row goes once
	// the upload succeeds: the file then appears in the list the caller passes in.
	const upload = (file: File, key: number): void => {
		if (!beginOperation()) {
			return;
		}
		setPendingUploads(uploads =>
			uploads.some(pending => pending.key === key)
				? uploads.map(pending => (pending.key === key ? {...pending, progress: 0, failed: false} : pending))
				: [...uploads, {key, file, progress: 0, failed: false}]
		);
		onAttach(file, fraction => updatePending(key, {progress: fraction}))
			.then(
				whileMounted(() => setPendingUploads(uploads => uploads.filter(pending => pending.key !== key))),
				() => updatePending(key, {failed: true})
			)
			.finally(endOperation);
	};

	const retryUpload = (key: number): void => {
		const pending = pendingUploads.find(candidate => candidate.key === key);
		if (pending) {
			upload(pending.file, key);
		}
	};

	const dismissUpload = (key: number): void => {
		setPendingUploads(uploads => uploads.filter(pending => pending.key !== key));
	};

	// Guard the operation, not just the Browse button: the drop zone and the hidden input stay live
	// during an upload, so without this a second attach starts while the first is in flight — the
	// second's setError(null) wipes the first's failure message, and whichever settles first clears the
	// busy state while the other is still running.
	const attach = (file: File | undefined): void => {
		if (!file || inFlightRef.current) {
			return;
		}
		const validation = validateNovaFile(file);
		if (!validation.ok) {
			setError(
				formatMessage({
					id: validation.reason === 'size' ? 'nova.referenceFiles.errorSize' : 'nova.referenceFiles.errorType',
				})
			);
			return;
		}
		if (trackUploads) {
			upload(file, nextUploadKeyRef.current++);
			return;
		}
		if (!beginOperation()) {
			return;
		}
		onAttach(file)
			.catch(whileMounted(() => setError(formatMessage({id: 'nova.referenceFiles.attachError'}))))
			.finally(endOperation);
	};

	const remove = (fileId: string): void => {
		if (!beginOperation()) {
			return;
		}
		onRemove(fileId)
			.catch(whileMounted(() => setError(formatMessage({id: 'nova.referenceFiles.removeError'}))))
			.finally(endOperation);
	};

	const dropZoneHandlers = {
		onDragOver: (event: React.DragEvent<HTMLDivElement>): void => {
			event.preventDefault();
			setDragActive(true);
		},
		onDragLeave: (): void => setDragActive(false),
		onDrop: (event: React.DragEvent<HTMLDivElement>): void => {
			event.preventDefault();
			setDragActive(false);
			attach(event.dataTransfer.files[0]);
		},
	};

	const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
		attach(event.target.files?.[0]);
		// Clear the value so re-selecting the same file still fires onChange.
		event.target.value = '';
	};

	return {
		inputRef,
		dragActive,
		isBusy,
		error,
		copied,
		copyToken,
		remove,
		dropZoneHandlers,
		handleInputChange,
		pendingUploads,
		retryUpload,
		dismissUpload,
	};
}
